import React, { useCallback, useMemo, useState } from "react";
import { useLocale } from "../../../locales";

export interface TextToSpeechSubmitPayload {
  voice_id: string;
  name?: string;
  model_id?: string;
  language_code?: string;
  output_format?: string;
  voice_settings?: {
    speed: number;
  };
}

interface TextToSpeechModalProps {
  text: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: TextToSpeechSubmitPayload) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const parseSelectOptions = (value?: string): SelectOption[] => {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((entry) => {
      const [value, label] = entry.split(":");
      const trimmedValue = value?.trim();
      if (!trimmedValue) {
        return null;
      }
      return {
        value: trimmedValue,
        label: label?.trim() || trimmedValue,
      };
    })
    .filter((option): option is SelectOption => Boolean(option));
};

const DEFAULT_OUTPUT_FORMAT_OPTIONS: SelectOption[] = [
  { value: "mp3_44100_128", label: "MP3" },
];

const DEFAULT_MODEL_OPTIONS: SelectOption[] = [
  { value: "eleven_v3", label: "eleven_v3" },
  { value: "eleven_multilingual_v2", label: "eleven_multilingual_v2" },
];

const DEFAULT_VOICE_OPTIONS: SelectOption[] = [
  {
    value: "KbaseEXyT9EE0CQLEfbB",
    label: "Belma - Dynamic, Playful, Clear Narrator",
  },
  {
    value: "IuRRIAcbQK5AQk1XevPj",
    label: "Doga - Deep, Clear, Professional",
  },
  {
    value: "iq1GS1mcjc63xtqTrsFh",
    label: "Pixel - eLearning for Kids! Fun and engaging",
  },
];

const buildDefaultName = (text: string): string => {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "";
  }
  return normalized.length > 40
    ? `${normalized.slice(0, 40).trim()}...`
    : normalized;
};

const TextToSpeechModal: React.FC<TextToSpeechModalProps> = ({
  text,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const { t } = useLocale();
  const voiceOptions = useMemo(
    () => {
      const configuredOptions = parseSelectOptions(
        process.env.REACT_APP_TTS_VOICES
      );
      const options = configuredOptions.length > 0
        ? configuredOptions
        : DEFAULT_VOICE_OPTIONS;
      const defaultVoiceId = process.env.REACT_APP_ELEVENLABS_DEFAULT_VOICE_ID;

      if (
        defaultVoiceId &&
        !options.some((option) => option.value === defaultVoiceId)
      ) {
        return [
          { value: defaultVoiceId, label: defaultVoiceId },
          ...options,
        ];
      }

      return options;
    },
    []
  );
  const outputFormatOptions = useMemo(() => {
    const configuredOptions = parseSelectOptions(
      process.env.REACT_APP_TTS_OUTPUT_FORMATS
    );
    return configuredOptions.length > 0
      ? configuredOptions
      : DEFAULT_OUTPUT_FORMAT_OPTIONS;
  }, []);
  const modelOptions = useMemo(() => {
    const configuredOptions = parseSelectOptions(
      process.env.REACT_APP_TTS_MODELS
    );
    const options = configuredOptions.length > 0
      ? configuredOptions
      : DEFAULT_MODEL_OPTIONS;
    const defaultModelId = process.env.REACT_APP_ELEVENLABS_MODEL_ID;

    if (
      defaultModelId &&
      !options.some((option) => option.value === defaultModelId)
    ) {
      return [
        { value: defaultModelId, label: defaultModelId },
        ...options,
      ];
    }

    return options;
  }, []);
  const defaultVoiceId = voiceOptions[0]?.value || "";
  const defaultConfiguredModelId =
    process.env.REACT_APP_ELEVENLABS_MODEL_ID || "eleven_v3";

  const [voiceId, setVoiceId] = useState(defaultVoiceId);
  const [name, setName] = useState(buildDefaultName(text));
  const [modelId, setModelId] = useState(
    modelOptions.some((option) => option.value === defaultConfiguredModelId)
      ? defaultConfiguredModelId
      : modelOptions[0]?.value || ""
  );
  const languageCode = process.env.REACT_APP_ELEVENLABS_LANGUAGE_CODE || "tr";
  const [outputFormat, setOutputFormat] = useState(
    outputFormatOptions.some(
      (option) => option.value === process.env.REACT_APP_ELEVENLABS_OUTPUT_FORMAT
    )
      ? process.env.REACT_APP_ELEVENLABS_OUTPUT_FORMAT!
      : outputFormatOptions[0]?.value || ""
  );
  const [speed, setSpeed] = useState(1);

  const canSubmit = voiceId.trim().length > 0 && text.trim().length > 0;

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit || isSubmitting) {
        return;
      }

      onSubmit({
        voice_id: voiceId.trim(),
        name: name.trim() || undefined,
        model_id: modelId.trim() || undefined,
        language_code: languageCode.trim() || undefined,
        output_format: outputFormat.trim() || undefined,
        voice_settings: {
          speed,
        },
      });
    },
    [canSubmit, isSubmitting, languageCode, modelId, name, onSubmit, outputFormat, speed, voiceId]
  );

  return (
    <div className="qe-modal" role="presentation">
      <button
        type="button"
        className="qe-modal__backdrop"
        onClick={isSubmitting ? undefined : onClose}
        aria-label={t("tts.cancel")}
      />
      <form
        className="qe-modal__content qe-modal__content--tts"
        onSubmit={handleSubmit}
      >
        <div className="qe-modal__header">
          <div>
            <h2>{t("tts.title")}</h2>
            <p className="qe-modal__subtitle">{t("tts.subtitle")}</p>
          </div>
        </div>

        <div className="qe-modal__body">
          <div className="qe-modal__field">
            <span className="qe-form__label">{t("tts.text")}</span>
            <div className="qe-tts-preview">{text}</div>
          </div>

          <div className="qe-modal__field">
            <span className="qe-form__label">{t("tts.voiceId")}</span>
            <select
              className="qe-input"
              value={voiceId}
              onChange={(event) => setVoiceId(event.target.value)}
              disabled={isSubmitting}
            >
              {voiceOptions.length === 0 ? (
                <option value="">{t("tts.selectVoice")}</option>
              ) : null}
              {voiceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="qe-modal__field">
            <span className="qe-form__label">{t("tts.name")}</span>
            <input
              className="qe-input"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("tts.namePlaceholder")}
              disabled={isSubmitting}
            />
          </div>

          <div className="qe-modal__grid">
            <div className="qe-modal__field">
              <span className="qe-form__label">{t("tts.modelId")}</span>
              <select
                className="qe-input"
                value={modelId}
                onChange={(event) => setModelId(event.target.value)}
                disabled={isSubmitting}
              >
                {modelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="qe-modal__field">
              <span className="qe-form__label">{t("tts.languageCode")}</span>
              <input
                className="qe-input qe-input--readonly"
                type="text"
                maxLength={2}
                value={languageCode}
                readOnly
              />
            </div>
          </div>

          <div className="qe-modal__field">
            <span className="qe-form__label">{t("tts.outputFormat")}</span>
            <select
              className="qe-input"
              value={outputFormat}
              onChange={(event) => setOutputFormat(event.target.value)}
              disabled={isSubmitting}
            >
              {outputFormatOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="qe-modal__field">
            <div className="qe-modal__row qe-modal__row--spread">
              <span className="qe-form__label">{t("tts.speed")}</span>
              <span className="qe-tts-speed-value">{speed.toFixed(2)}x</span>
            </div>
            <input
              className="qe-tts-speed-slider"
              type="range"
              min={0.7}
              max={1.2}
              step={0.05}
              value={speed}
              onChange={(event) => setSpeed(Number(event.target.value))}
              disabled={isSubmitting}
            />
            <div className="qe-tts-speed-scale" aria-hidden="true">
              <span>0.7x</span>
              <span>1.0x</span>
              <span>1.2x</span>
            </div>
          </div>
        </div>

        <div className="qe-modal__footer">
          <button
            type="button"
            className="qe-button qe-button--secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("tts.cancel")}
          </button>
          <button
            type="submit"
            className="qe-button qe-button--primary"
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? t("tts.generating") : t("tts.generate")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TextToSpeechModal;
